import React, { useState, useCallback, createContext, useContext, useEffect } from "react";

const ToastContext = createContext(null);
export const useToast = () => useContext(ToastContext);

let toastId = 0;

const ToastItem = ({ id, message, type, duration, onRemove }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const step = 100 / (duration / 10);
    const interval = setInterval(() => {
      setProgress(prev => Math.max(0, prev - step));
    }, 10);
    return () => clearInterval(interval);
  }, [duration]);

  const config = {
    success: { icon: "✅", color: "#10b981", bg: "rgba(16, 185, 129, 0.95)" },
    error:   { icon: "❌", color: "#ef4444", bg: "rgba(239, 68, 68, 0.95)" },
    info:    { icon: "ℹ️", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.95)" },
    warn:    { icon: "⚠️", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.95)" },
  };

  const style = config[type] || config.info;

  return (
    <div style={{
      background: "#1e2128",
      border: `1px solid ${style.color}40`,
      borderRadius: "14px",
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      width: "calc(100vw - 40px)",
      maxWidth: "400px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
      animation: "toastMobileUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
      position: "relative",
      overflow: "hidden",
      pointerEvents: "all",
      marginBottom: "12px",
      backdropFilter: "blur(10px)"
    }}>
      <div style={{
        minWidth: "32px", height: "32px", borderRadius: "50%",
        background: `${style.color}20`, display: "flex",
        alignItems: "center", justifyContent: "center", fontSize: "16px"
      }}>
        {style.icon}
      </div>
      <div style={{ flex: 1, fontSize: "13px", fontWeight: 600, color: "white", lineHeight: "1.4" }}>
        {message}
      </div>
      <div style={{
        position: "absolute", bottom: 0, left: 0,
        height: "3px", width: `${progress}%`,
        background: style.color,
        transition: "width 0.01s linear"
      }} />
    </div>
  );
};

export const GlobalToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "info", duration = 3000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type, duration }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const toast = {
    success: (msg, dur) => addToast(msg, "success", dur),
    error: (msg, dur) => addToast(msg, "error", dur),
    info: (msg, dur) => addToast(msg, "info", dur),
    warn: (msg, dur) => addToast(msg, "warn", dur),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div style={{
        position: "fixed", bottom: "30px", left: "50%",
        transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center",
        zIndex: 1000000, pointerEvents: "none"
      }}>
        {toasts.map(t => (
          <ToastItem key={t.id} {...t} onRemove={() => setToasts(prev => prev.filter(item => item.id !== t.id))} />
        ))}
      </div>
      <style>{`
        @keyframes toastMobileUp {
          from { opacity: 0; transform: translateY(40px) scale(0.9); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </ToastContext.Provider>
  );
};
